import {
  OSU_PLAYFIELD_HEIGHT,
  OSU_PLAYFIELD_WIDTH,
  type CircleHitObject,
  type CirclesChart,
} from '../../circlesChart.js';
import {
  GOOD_WINDOW_MS,
  HP_EMPTY_TAP,
  HP_EMPTY_TAP_GRACE_HITS,
  HP_EMPTY_TAP_NEARBY_BEATS,
  HP_GOOD,
  HP_MISS_DAMAGE_BY_STREAK_INDEX,
  HP_OK,
  HP_PERFECT,
  HP_REGEN_INTERVAL_BEATS,
  HP_REGEN_PER_TICK,
  HP_REGEN_QUIET_BEATS,
  LIFELINE_HP_THRESHOLD,
  MAX_HP,
  OK_WINDOW_MS,
  PERFECT_WINDOW_MS,
  SLIDER_FOLLOW_RADIUS_MULTIPLIER,
  SLIDER_REPEAT_SCORE,
  SLIDER_TAIL_SCORE,
  SLIDER_TICK_SCORE,
  SPINNER_BONUS_ROTATIONS_THRESHOLD,
  SPINNER_CLEAR_BONUS_PER_ROTATION,
  SPINNER_MAX_RPS,
  SPINNER_POINTS_PER_RADIAN,
} from '../../gameTypes.js';
import { buildSliderPath, pointAtDistance, type SliderPath, type Vec2 } from '../../osuCurves.js';
import { sliderArcLengthAt } from '../../osuSliderTiming.js';
import { comboMultiplier, computeAccuracy, judge } from '../../scoring.js';
import {
  buildHitObjectLookup,
  type CircleResolution,
  type CirclesEngineConfig,
  type CirclesEngineState,
  type SliderProgress,
} from './state.js';

/**
 * One player input. The Circles input model has two channels:
 *  - {@link CirclesEngineInput}: discrete pointer-down taps, one event
 *    per click with the cursor position at that instant.
 *  - {@link CirclesSpinSample}: continuous cursor positions captured
 *    during active spinner OR slider windows. Spinners read
 *    `(beat, x, y)` to credit rotation; sliders additionally read
 *    `pressing` to gate tick / repeat / tail awards on whether the
 *    player was holding the tap button at that frame.
 *
 * Replay verification stays deterministic because both channels carry
 * plain primitives — the server re-feeds the same samples through the
 * same engine and reproduces the same scoring decisions.
 */
export interface CirclesEngineInput {
  beat: number;
  x: number;
  y: number;
}

/**
 * One captured cursor position during an active spinner OR slider.
 * Engines process samples chronologically (sorted by beat with taps);
 * each sample contributes its angular delta to any active spinner
 * AND updates the tracking state of any active slider.
 *
 * `pressing` is optional for backwards compatibility — legacy
 * spinner-only replays predate slider tracking and omit it. The
 * slider engine treats `undefined` and `false` identically (no
 * tracking → no body credit); spinner engine ignores the field
 * entirely.
 */
export interface CirclesSpinSample {
  beat: number;
  x: number;
  y: number;
  pressing?: boolean;
}

export interface CirclesHpChangedPayload {
  delta: number;
  hp: number;
  reason:
    | 'perfect'
    | 'good'
    | 'ok'
    | 'miss'
    | 'empty_tap'
    | 'regen'
    | 'lifeline'
    | 'drained';
}

export interface CirclesResolvedPayload extends CircleResolution {}

export interface CirclesSpinTickPayload {
  hitObjectId: string;
  /** Total accumulated rotations (radians / 2π) on this spinner so far. */
  rotations: number;
  /** Unsigned radian delta credited by this sample. */
  deltaRadians: number;
  /**
   * Rotations per second derived from {@link deltaRadians} and the
   * beat-time elapsed since the previous sample. Cosmetic / SFX only
   * — scoring uses {@link deltaRadians} directly. Always positive
   * because zero-delta samples are filtered out before event emit.
   */
  rps: number;
  /** Beat at which this sample was captured. */
  beat: number;
  /** Score points credited by this sample (already added to state.score). */
  scoreDelta: number;
  /**
   * Score lump this tick credited from clear-bonus rotations — full
   * rotations strictly past
   * {@link import('../../gameTypes.js').SPINNER_BONUS_ROTATIONS_THRESHOLD}.
   * Already multiplied by the spin-start combo multiplier and is a
   * subset of {@link scoreDelta} (so the client doesn't add it
   * again). Renderer uses this to fire the dramatic "+N" pop and
   * the bell SFX so the late-spinner payoff reads as a discrete
   * event the way osu!'s post-clear bonus does.
   *
   * 0 (default) means the tick crossed no bonus boundaries. Can be
   * > 1 × {@link SPINNER_CLEAR_BONUS_PER_ROTATION} when a single
   * large sample crosses multiple full rotations at once — the
   * client should still fire one consolidated burst with the right
   * total, not N separate "+1000"s.
   */
  bonusScoreDelta: number;
  /**
   * Number of full clear-bonus rotations awarded this tick. Always
   * 0 unless {@link bonusScoreDelta} > 0; ≥1 when crossed.
   */
  bonusRotationsCrossed: number;
}

/**
 * Payload accompanying the `circle_shake` event the engine emits when
 * a tap on an approaching circle is judged early-click but the player
 * was clearly aiming at it (radius hit, just outside the timing
 * window). Lets the renderer flash a brief "TOO EARLY" cue without
 * costing HP or breaking combo.
 */
export interface CirclesCircleShakePayload {
  hitObjectId: string;
}

/**
 * Payload for `slider_tick` / `slider_repeat` / `slider_tail` events.
 * The engine fires one per crossed beat per slider that received a
 * tracking sample at that point. `tracking` records whether the
 * tick / repeat / tail awarded score (true) or was a tracking-loss
 * fallthrough (false — no score, no combo bump).
 */
export interface CirclesSliderTickPayload {
  hitObjectId: string;
  /** Tick beat that fired (or repeat / tail beat, depending on event). */
  beat: number;
  /** True when the player was tracking at this beat → award fired. */
  tracking: boolean;
  /** Score added to `state.score` from this event (0 when not tracking). */
  scoreDelta: number;
}

export interface CirclesEngineEvent {
  type:
    | 'tap'
    | 'object_resolved'
    | 'completed'
    | 'hp_changed'
    | 'spinner_resolved'
    | 'spinner_tick'
    | 'slider_tick'
    | 'slider_repeat'
    | 'slider_tail'
    | 'circle_shake';
  beat: number;
  payload?: unknown;
}

export interface CirclesStepInput {
  config: CirclesEngineConfig;
  state: CirclesEngineState;
  fromBeat: number;
  toBeat: number;
  inputs: CirclesEngineInput[];
  /**
   * Cursor samples captured during active spinner windows in this
   * step. Samples landing outside any active spinner are silently
   * dropped (no spinner to credit). Caller is not required to sort —
   * the step function merges samples and taps into chronological
   * order before processing.
   */
  spinSamples?: CirclesSpinSample[];
}

export interface CirclesStepOutput {
  state: CirclesEngineState;
  events: CirclesEngineEvent[];
}

export function bpmToBeatsPerMs(bpm: number): number {
  return bpm / 60_000;
}

/**
 * Lower bound for the half-window in beats so a totally unset perfect
 * window never collapses to zero (which would make it impossible to
 * register hits). Mirrors {@link import('../pulse/core.js').buildEngineConfig}.
 */
const HIT_HALF_BEAT_WINDOW_FALLBACK = 0.001;

export function buildCirclesEngineConfig(chart: CirclesChart): CirclesEngineConfig {
  const beatsPerMs = bpmToBeatsPerMs(chart.bpm);
  // Per-chart window overrides let Hard / Extreme variants tighten
  // the timing tolerance. Falling back to the global constants
  // keeps every existing chart and test fixture working unchanged.
  const perfectMs = chart.perfectWindowMs ?? PERFECT_WINDOW_MS;
  const goodMs = chart.goodWindowMs ?? GOOD_WINDOW_MS;
  // OK tier defaults to OK_WINDOW_MS so every chart inherits the
  // mercy band by default. Chart authors can clamp it equal to
  // goodMs to disable the band on harder variants.
  const rawOkMs = chart.okWindowMs ?? OK_WINDOW_MS;
  // Guard against authoring mistakes: the validator rejects okMs <
  // goodMs, but a chart that ships without an okWindowMs override
  // and a tightened goodWindowMs (e.g. Extreme variants at 80 ms)
  // must still satisfy `goodMs <= okMs`. Floor at goodMs so the
  // OK band collapses cleanly instead of inverting.
  // Clamp against BOTH the good window and the perfect window. The
  // validator catches the obvious case (chart-supplied perfect > ok)
  // but we defend in depth: if a future chart sets a perfectWindow
  // larger than the global ok fallback (or vice versa), `judge()`'s
  // tier ordering must still hold or the lower tiers become
  // unreachable.
  const okMs = Math.max(goodMs, perfectMs, rawOkMs);
  // Approach time: chart-supplied ms takes precedence (BPM-independent
  // reading window, osu!-faithful), falling back to chart.approachBeats
  // (BPM-coupled). Either way we expose both representations on the
  // config so consumers (engine, renderer, session) can read whichever
  // form they need without re-deriving.
  const msPerBeat = beatsPerMs > 0 ? 1 / beatsPerMs : 0;
  const approachMs = chart.approachMs ?? chart.approachBeats * msPerBeat;
  const approachBeats = beatsPerMs > 0 ? approachMs * beatsPerMs : chart.approachBeats;
  // Build slider paths once per config — `buildSliderPath` tessellates
  // each curve into a few hundred points and computes prefix
  // arc-lengths, which is too expensive to redo per cursor sample.
  // The engine reads from this map via `pointAtDistance` per sample.
  // Sliders missing a payload (e.g. imported with malformed geometry
  // that degraded to circle in the importer) won't have `kind ===
  // 'slider'` so we never look them up — but we defensively check
  // both kind AND slider-data presence here to keep the map shape
  // consistent with what the engine expects.
  const sliderPaths = new Map<string, SliderPath>();
  for (const obj of chart.hitObjects) {
    if (obj.kind === 'slider' && obj.slider) {
      // `buildSliderPath` tessellates the geometric curve; the
      // slider's authored `length` is the runtime clamp passed to
      // `pointAtDistance(path, distance)` (curves can geometrically
      // be longer than the slider's authored arc-length, e.g.
      // perfect-circle splines drawn through 3 points whose chord
      // length differs from the .osu-spec'd `length`). Both halves
      // of the contract are honoured at query time, not build time.
      //
      // Prepend the head: chart `controlPoints` are stored
      // post-head (matches the source `.osu` row's `<x>:<y>|...`
      // convention — see `SliderHitData.controlPoints`).
      // `buildSliderPath` expects the head to BE index 0, so we
      // splice it in once here per chart load.
      const cps: Vec2[] = [{ x: obj.x, y: obj.y }, ...obj.slider.controlPoints];
      sliderPaths.set(obj.id, buildSliderPath(obj.slider.curveType, cps));
    }
  }
  return {
    chart,
    // Sort once per config build so every step-loop scan iterates a
    // canonical order. Crucially this is what the server's headless
    // runner sees too, so client and server agree on tie-break order
    // even if the chart JSON is re-serialised by some future tool.
    sortedHitObjects: buildHitObjectLookup(chart.hitObjects),
    perfectWindowMs: perfectMs,
    goodWindowMs: goodMs,
    okWindowMs: okMs,
    perfectWindowBeats: Math.max(perfectMs * beatsPerMs, HIT_HALF_BEAT_WINDOW_FALLBACK),
    goodWindowBeats: Math.max(goodMs * beatsPerMs, HIT_HALF_BEAT_WINDOW_FALLBACK),
    okWindowBeats: Math.max(okMs * beatsPerMs, HIT_HALF_BEAT_WINDOW_FALLBACK),
    approachBeats,
    approachMs,
    sliderPaths,
  };
}

function applyHpDelta(
  state: CirclesEngineState,
  beat: number,
  delta: number,
  reason: CirclesHpChangedPayload['reason'],
  events: CirclesEngineEvent[],
): void {
  if (delta === 0) {
    return;
  }
  if (delta < 0) {
    state.combo = 0;
    state.regenProgressBeats = 0;
  }
  const wasHealthy = state.hp > LIFELINE_HP_THRESHOLD;
  const oldHp = state.hp;
  const rawNewHp = state.hp + delta;
  let lifelineCaught = false;
  if (delta < 0 && wasHealthy && rawNewHp <= 0) {
    state.hp = 1;
    lifelineCaught = true;
  } else {
    state.hp = Math.max(0, Math.min(state.maxHp, rawNewHp));
  }
  const drained = state.hp <= 0;
  const effectiveReason: CirclesHpChangedPayload['reason'] = drained
    ? 'drained'
    : lifelineCaught
      ? 'lifeline'
      : reason;
  const payload: CirclesHpChangedPayload = {
    delta: state.hp - oldHp,
    hp: state.hp,
    reason: effectiveReason,
  };
  events.push({ type: 'hp_changed', beat, payload });
  if (drained) {
    state.alive = false;
    state.deathReason = 'drained';
    state.deathBeat = beat;
  }
}

function missDamageForStreak(streak: number): number {
  const table = HP_MISS_DAMAGE_BY_STREAK_INDEX;
  const idx = Math.min(streak, table.length - 1);
  return table[idx];
}

/**
 * Bookkeep a missed circle: streak + last-miss beat, decaying HP damage.
 */
function applyMissDamage(
  state: CirclesEngineState,
  beat: number,
  events: CirclesEngineEvent[],
): void {
  const damage = missDamageForStreak(state.consecutiveMissCount);
  state.consecutiveMissCount += 1;
  state.lastMissBeat = beat;
  applyHpDelta(state, beat, damage, 'miss', events);
}

/**
 * Whether any unresolved CIRCLE lies within HP_EMPTY_TAP_NEARBY_BEATS
 * of the given beat. Mirrors the Pulse rule: empty taps in dead air
 * are free, but tapping when there's a real target close enough in
 * time that the player could plausibly have been reacting to it costs
 * HP (after the onboarding grace).
 *
 * Spinners are deliberately excluded. They auto-resolve and have no
 * tap surface — the player CAN'T hit a spinner with a tap, so
 * tapping at any beat during a spinner span isn't "an attempt at the
 * spinner" the way an off-beat tap near a circle is "an attempt at
 * the circle". Counting spinners here would penalise the player for
 * mash-tapping during long spinners, which is exactly what spinners
 * should be a free zone for.
 */
function hasUnresolvedObjectWithinEmptyTapRange(
  hitObjects: ReadonlyArray<CircleHitObject>,
  resolvedIds: ReadonlySet<string>,
  beat: number,
): boolean {
  for (const obj of hitObjects) {
    if (resolvedIds.has(obj.id)) {
      continue;
    }
    if (obj.kind === 'spinner') {
      continue;
    }
    if (Math.abs(obj.beat - beat) <= HP_EMPTY_TAP_NEARBY_BEATS) {
      return true;
    }
  }
  return false;
}

/**
 * Locate the unresolved circle whose centre best matches a (beat, x, y)
 * tap. The chart validator guarantees `circleRadius > 0`, so two
 * conditions must hold:
 *   1. timing — `|tap.beat - object.beat| <= okWindowBeats` (the
 *      widest accepting tier; sub-tiers are partitioned in
 *      {@link resolveCircleHit} via `judge()`)
 *   2. position — Euclidean distance <= circleRadius
 *
 * If multiple candidates qualify we pick the one closest in beat-time;
 * this matches osu! note-priority where players "feel" the next note
 * as the active target. Spinners are ignored here — they can't be tapped
 * and resolve via auto-expiry.
 */
function findClosestOpenHitObjectForTap(
  hitObjects: ReadonlyArray<CircleHitObject>,
  resolvedIds: ReadonlySet<string>,
  input: CirclesEngineInput,
  okWindowBeats: number,
  circleRadius: number,
): { hitObject: CircleHitObject; distance: number } | null {
  let best: { hitObject: CircleHitObject; diff: number; distance: number } | null = null;
  const radiusSq = circleRadius * circleRadius;
  for (const obj of hitObjects) {
    if (obj.kind === 'spinner') {
      continue;
    }
    if (resolvedIds.has(obj.id)) {
      continue;
    }
    const diff = Math.abs(input.beat - obj.beat);
    if (diff > okWindowBeats) {
      continue;
    }
    const dx = obj.x - input.x;
    const dy = obj.y - input.y;
    const distSq = dx * dx + dy * dy;
    if (distSq > radiusSq) {
      continue;
    }
    if (!best || diff < best.diff) {
      best = { hitObject: obj, diff, distance: Math.sqrt(distSq) };
    }
  }
  if (!best) {
    return null;
  }
  return { hitObject: best.hitObject, distance: best.distance };
}

/**
 * Locate a visible-but-not-yet-hittable circle the player clicked on.
 * Used as a fallback after `findClosestOpenHitObjectForTap` returns
 * null: instead of treating an early click on a real circle as an
 * empty tap (which would feel arbitrary — the player clearly aimed
 * at the circle), we resolve that circle as a miss and surface a
 * "Too Early!" cue.
 *
 * Bounded by the chart's `approachBeats` so a click far before the
 * circle is even rendered doesn't count — the player can't have been
 * trying to hit something they couldn't see. Only matches earlier
 * (`obj.beat - input.beat` strictly positive); late-side ticks past
 * the good window are auto-missed before this code can run, so the
 * search is one-sided.
 *
 * Tie-break: smallest `earlyBy` wins (the more-imminent circle is
 * consumed first). On exact ties the first-encountered candidate
 * sticks — `sortedHitObjects` orders by `(beat asc, id asc)`, so a
 * tie is broken by smallest beat then smallest id, deterministically.
 */
function findEarlyClickedCircle(
  hitObjects: ReadonlyArray<CircleHitObject>,
  resolvedIds: ReadonlySet<string>,
  input: CirclesEngineInput,
  approachBeats: number,
  okWindowBeats: number,
  circleRadius: number,
): { hitObject: CircleHitObject; distance: number } | null {
  let best: { hitObject: CircleHitObject; earlyBy: number; distance: number } | null = null;
  const radiusSq = circleRadius * circleRadius;
  for (const obj of hitObjects) {
    if (obj.kind === 'spinner') {
      continue;
    }
    if (resolvedIds.has(obj.id)) {
      continue;
    }
    // earlyBy > 0 means the player tapped before the circle's hit beat.
    // Strict `>` against okWindowBeats so a click sitting exactly on
    // the boundary is judged by `findClosestOpenHitObjectForTap` (it
    // also uses `<= okWindowBeats`), preventing both branches from
    // claiming the same edge tap. Bound is the widest accepting tier
    // because anything inside the OK band is now a real hit, not an
    // early click.
    const earlyBy = obj.beat - input.beat;
    if (earlyBy <= okWindowBeats || earlyBy > approachBeats) {
      continue;
    }
    const dx = obj.x - input.x;
    const dy = obj.y - input.y;
    const distSq = dx * dx + dy * dy;
    if (distSq > radiusSq) {
      continue;
    }
    if (!best || earlyBy < best.earlyBy) {
      best = { hitObject: obj, earlyBy, distance: Math.sqrt(distSq) };
    }
  }
  if (!best) {
    return null;
  }
  return { hitObject: best.hitObject, distance: best.distance };
}

function resolveCircleHit(
  state: CirclesEngineState,
  config: CirclesEngineConfig,
  hitObject: CircleHitObject,
  input: CirclesEngineInput,
  distance: number,
  events: CirclesEngineEvent[],
): CircleResolution {
  const beatsPerMs = bpmToBeatsPerMs(config.chart.bpm);
  const diffMs = (input.beat - hitObject.beat) / beatsPerMs;
  // Per-chart windows so harder variants demand tighter timing. The
  // OK tier is opt-in: the engine only ever passes okWindowMs (so a
  // chart that doesn't override still gets the global mercy band),
  // never None. Pulse never reaches this function — it has its own
  // judge() callsite with no okWindowMs.
  const j = judge(diffMs, 0, {
    perfectWindowMs: config.perfectWindowMs,
    goodWindowMs: config.goodWindowMs,
    okWindowMs: config.okWindowMs,
  });
  // Snapshot the pre-resolution combo so the event payload reports
  // it accurately. The session uses this to detect combo breaks
  // per-event (was-N-now-0) without re-deriving from `state.combo`,
  // which has already mutated by the time the consumer reads it.
  const previousCombo = state.combo;

  state.resolvedIds.add(hitObject.id);
  const resolution: CircleResolution = {
    hitObjectId: hitObject.id,
    verdict: j.verdict,
    diffMs,
    distance,
    beat: input.beat,
    previousCombo,
  };
  state.resolutions.push(resolution);

  if (j.verdict === 'perfect') {
    state.perfects += 1;
    state.combo += 1;
    state.score += Math.round(j.baseScore * comboMultiplier(state.combo));
  } else if (j.verdict === 'good') {
    state.goods += 1;
    state.combo += 1;
    state.score += Math.round(j.baseScore * comboMultiplier(state.combo));
  } else if (j.verdict === 'ok') {
    state.oks += 1;
    state.combo += 1;
    state.score += Math.round(j.baseScore * comboMultiplier(state.combo));
  } else {
    state.misses += 1;
    state.combo = 0;
  }
  if (state.combo > state.maxCombo) {
    state.maxCombo = state.combo;
  }
  events.push({ type: 'object_resolved', beat: input.beat, payload: resolution });

  if (j.verdict === 'perfect') {
    state.consecutiveMissCount = 0;
    applyHpDelta(state, input.beat, HP_PERFECT, 'perfect', events);
  } else if (j.verdict === 'good') {
    state.consecutiveMissCount = 0;
    applyHpDelta(state, input.beat, HP_GOOD, 'good', events);
  } else if (j.verdict === 'ok') {
    state.consecutiveMissCount = 0;
    applyHpDelta(state, input.beat, HP_OK, 'ok', events);
  } else {
    applyMissDamage(state, input.beat, events);
  }

  // Slider head taps: snapshot progress so subsequent cursor samples
  // can drive tick / repeat / tail awards. We lock the combo
  // multiplier at this moment (mirrors spinner pattern) so live
  // client + headless replay produce identical body-component
  // scores regardless of how mid-slider hits would have nudged the
  // multiplier between samples. A 'miss' verdict still creates the
  // entry — `processSliderBeatsCrossed` reads `headVerdict` to
  // suppress body credit on missed-head sliders, but the entry's
  // existence lets the renderer fade-out the missed slider visual
  // without re-querying the resolution event.
  if (hitObject.kind === 'slider' && hitObject.slider) {
    state.sliderProgress.set(hitObject.id, {
      headVerdict: j.verdict,
      headResolved: true,
      lastSampleBeat: null,
      trackingNow: false,
      ticksHit: 0,
      repeatsHit: 0,
      tailHit: false,
      tailFired: false,
      nextTickIdx: 0,
      nextRepeatIdx: 0,
      comboMultiplierAtHead: comboMultiplier(state.combo),
    });
  }

  return resolution;
}

/**
 * Process auto-resolutions due as of `toBeat` in chronological order
 * across BOTH kinds (missed circles AND finished spinners). Resolving
 * each kind in beat order keeps combo / HP numerically consistent
 * across step boundaries, and we abort the loop as soon as `state.alive`
 * flips false (an HP-drain on a streak of missed circles).
 *
 * v1 spinner design: spinners are non-tappable filler so the timing
 * density of the source map stays faithful, but they're not failable
 * — players hate dying to a mechanic they can't even see (no spin
 * counter UI yet). Each completed spinner banks a synthetic `good`
 * (zero HP cost, partial score, combo+1) so a song full of spinners
 * doesn't tank the score.
 */
interface PendingAutoResolution {
  obj: CircleHitObject;
  resolveBeat: number;
}

function expireDueAutoResolutions(
  state: CirclesEngineState,
  config: CirclesEngineConfig,
  toBeat: number,
  events: CirclesEngineEvent[],
): void {
  const due: PendingAutoResolution[] = [];

  for (const obj of config.sortedHitObjects) {
    if (state.resolvedIds.has(obj.id)) {
      continue;
    }
    if (obj.kind === 'spinner') {
      const endBeat = obj.spinnerEndBeat ?? obj.beat;
      if (toBeat >= endBeat) {
        due.push({ obj, resolveBeat: endBeat });
      }
    } else {
      // Circle stays clickable until the late edge of the widest
      // accepting tier (OK band). This is what gives the OK tier
      // its mercy: a player tapping ~150 ms late still gets to
      // resolve the circle as OK rather than the auto-miss
      // beating their tap to it.
      const expiryBeat = obj.beat + config.okWindowBeats;
      if (toBeat > expiryBeat) {
        due.push({ obj, resolveBeat: obj.beat });
      }
    }
  }

  // Sort by chronological resolution beat. Tie-breakers (deterministic):
  //   1. resolveBeat asc (chronology)
  //   2. circle before spinner at the same beat — short spinners that
  //      finish exactly when a circle expires would feel like the
  //      circle should land first (it actually arrived first
  //      musically; the spinner is filler around it)
  //   3. id asc (lexicographic) for full determinism
  // Both the headless runner and the live session call `step()` with
  // the same config, so this sort is identical client + server.
  due.sort((a, b) => {
    if (a.resolveBeat !== b.resolveBeat) {
      return a.resolveBeat - b.resolveBeat;
    }
    if (a.obj.kind !== b.obj.kind) {
      return a.obj.kind === 'circle' ? -1 : 1;
    }
    return a.obj.id < b.obj.id ? -1 : a.obj.id > b.obj.id ? 1 : 0;
  });

  for (const { obj, resolveBeat } of due) {
    if (obj.kind === 'spinner') {
      resolveSpinnerCompletion(state, obj, resolveBeat, events);
    } else {
      resolveCircleAutoMiss(state, config, obj, events);
    }
    if (!state.alive) {
      return;
    }
  }
}

function resolveCircleAutoMiss(
  state: CirclesEngineState,
  config: CirclesEngineConfig,
  obj: CircleHitObject,
  events: CirclesEngineEvent[],
): void {
  state.resolvedIds.add(obj.id);
  const resolution: CircleResolution = {
    hitObjectId: obj.id,
    verdict: 'miss',
    // Sentinel diffMs sits just past the widest accepting tier so a
    // consumer plotting auto-misses on a hit-error meter draws them
    // clearly outside any "could have been a hit" band.
    diffMs: config.okWindowMs + 1,
    distance: Number.POSITIVE_INFINITY,
    beat: obj.beat,
    previousCombo: state.combo,
  };
  state.resolutions.push(resolution);
  state.misses += 1;
  events.push({ type: 'object_resolved', beat: obj.beat, payload: resolution });
  applyMissDamage(state, obj.beat, events);
  // Sliders auto-miss the same way as circles when the head wasn't
  // tapped in time — but we still need a slider progress entry so
  // the body-scan knows to skip awards (headVerdict='miss' gates
  // tick / repeat / tail). The entry's other counters stay zero;
  // the renderer reads them to fade the doomed slider out without
  // an extra path lookup.
  if (obj.kind === 'slider' && obj.slider) {
    state.sliderProgress.set(obj.id, {
      headVerdict: 'miss',
      headResolved: true,
      lastSampleBeat: null,
      trackingNow: false,
      ticksHit: 0,
      repeatsHit: 0,
      tailHit: false,
      tailFired: false,
      nextTickIdx: 0,
      nextRepeatIdx: 0,
      // The body-component sweep skips miss-head sliders (gated on
      // `headVerdict === 'miss'`), so this field is never read for
      // an auto-missed slider. Use `comboMultiplier(state.combo)` for
      // parity with the hit path — `state.combo` is 0 here (just
      // reset by `applyMissDamage` above) so this resolves to 1, but
      // the symmetry keeps future readers from being surprised by a
      // hardcoded constant if `comboMultiplier`'s base ever changes.
      comboMultiplierAtHead: comboMultiplier(state.combo),
    });
  }
}

function resolveSpinnerCompletion(
  state: CirclesEngineState,
  obj: CircleHitObject,
  endBeat: number,
  events: CirclesEngineEvent[],
): void {
  state.resolvedIds.add(obj.id);
  const resolution: CircleResolution = {
    hitObjectId: obj.id,
    verdict: 'good',
    diffMs: 0,
    distance: 0,
    beat: endBeat,
    previousCombo: state.combo,
  };
  state.resolutions.push(resolution);
  state.goods += 1;
  state.combo += 1;
  if (state.combo > state.maxCombo) {
    state.maxCombo = state.combo;
  }
  state.consecutiveMissCount = 0;
  state.score += Math.round(100 * comboMultiplier(state.combo));
  events.push({ type: 'spinner_resolved', beat: endBeat, payload: resolution });
  applyHpDelta(state, endBeat, HP_GOOD, 'good', events);
}

const PLAYFIELD_CENTRE_X = OSU_PLAYFIELD_WIDTH / 2;
const PLAYFIELD_CENTRE_Y = OSU_PLAYFIELD_HEIGHT / 2;

/**
 * Find the unresolved spinner whose `[beat, spinnerEndBeat]` window
 * contains `beat`, or null if none is active. Spinners can overlap in
 * the chart format; we credit the earliest-starting active one so
 * stacked spinners drain their windows in chart order.
 *
 * Scans `sortedHitObjects` (beat-ascending) so the first match is by
 * construction the earliest-started active spinner — no extra sort
 * needed at sample time.
 *
 * Exported so the client session can gate per-frame spin-sample
 * capture on the same predicate the engine uses to credit them —
 * preventing wasted samples landing outside any spinner window from
 * bloating replays for no scoring effect.
 */
export function findActiveSpinner(
  sortedHitObjects: ReadonlyArray<CircleHitObject>,
  resolvedIds: ReadonlySet<string>,
  beat: number,
): CircleHitObject | null {
  // `sortedHitObjects` is beat-ascending, so once we pass an object
  // whose start-beat is past `beat` no later object can possibly be
  // active either. Bounds the scan at O(active spinners) instead of
  // O(all hit objects) — material for the 30k-sample-per-loop server
  // validation path on charts with hundreds of objects.
  for (const obj of sortedHitObjects) {
    if (obj.beat > beat) {
      break;
    }
    if (obj.kind !== 'spinner') {
      continue;
    }
    if (resolvedIds.has(obj.id)) {
      continue;
    }
    const endBeat = obj.spinnerEndBeat ?? obj.beat;
    if (beat <= endBeat) {
      return obj;
    }
  }
  return null;
}

/**
 * Find a slider whose `[beat, slider.endBeat]` window contains
 * `beat` and whose body could still legitimately receive credit
 * (head not yet auto-missed, tail not yet fired), or null if none
 * is in window. The session uses this to gate per-frame cursor
 * sample capture exactly like {@link findActiveSpinner} — samples
 * outside any in-window slider do nothing for scoring, and capturing
 * them anywhere else would bloat replays.
 *
 * Permissively includes pre-head sliders (no progress entry yet —
 * the player hasn't tapped the head yet but the slider's start beat
 * has been reached). Without this the head-tap frame would skip
 * sample capture: gating on `progress.headResolved` reads the engine
 * state BEFORE `step()` processes the head tap, so the very first
 * sample after the head click would be dropped. The engine's
 * `processSliderSample` cheaply skips samples whose slider has no
 * progress entry, so emitting one extra harmless sample per slider
 * is the right trade.
 *
 * Returns null on missed-head sliders and tail-fired sliders — both
 * states mean the body can no longer fire credit, so further samples
 * are pure replay bloat.
 */
export function findActiveSlider(
  sortedHitObjects: ReadonlyArray<CircleHitObject>,
  sliderProgress: ReadonlyMap<string, SliderProgress>,
  beat: number,
): CircleHitObject | null {
  for (const obj of sortedHitObjects) {
    if (obj.beat > beat) {
      break;
    }
    if (obj.kind !== 'slider' || !obj.slider) {
      continue;
    }
    if (beat > obj.slider.endBeat) {
      continue;
    }
    const progress = sliderProgress.get(obj.id);
    if (progress) {
      // Slider already has progress (head was tapped or auto-missed
      // on a previous step). Gate further samples on whether body
      // credit can still fire.
      if (progress.headVerdict === 'miss' || progress.tailFired) {
        continue;
      }
    }
    // No progress entry yet → pre-head; capturing this frame's
    // sample is fine and ensures the head-tap-frame sample isn't
    // dropped by an off-by-one against `step()`'s progress mutation.
    return obj;
  }
  return null;
}

/**
 * Smallest signed angular distance from `prev` to `next`, normalised
 * into `(-π, π]`. Used so a sweep across the atan2 discontinuity
 * (e.g. -3.1 rad → +3.1 rad as the cursor crosses the negative-x
 * axis) registers as a small forward step rather than a near-2π
 * leap. Direction-agnostic accumulation reads the absolute value
 * downstream — players can spin clockwise or anticlockwise with the
 * same scoring effect.
 */
function shortestAngleDelta(prev: number, next: number): number {
  let d = next - prev;
  while (d > Math.PI) {
    d -= 2 * Math.PI;
  }
  while (d <= -Math.PI) {
    d += 2 * Math.PI;
  }
  return d;
}

/**
 * Process one spinner sample. Looks up (or initialises) the
 * per-spinner progress entry, computes the angular delta against the
 * previous sample, applies the {@link SPINNER_MAX_RPS} cap, and
 * credits score + emits a `spinner_tick` event with the deltas the
 * client's renderer / SFX uses to drive juicy feedback.
 *
 * Score formula:
 *   `score += |deltaRad| * SPINNER_POINTS_PER_RADIAN * comboMultiplier(comboAtSpinStart)`
 *
 * The combo multiplier is locked at spin-start (first sample) so the
 * client (per-frame) and the server (per-step batched) accumulate in
 * the same chunks regardless of how the work is split — without this
 * lock, a circle hit landing mid-spinner would bump the multiplier on
 * the server's batched run before some samples were processed but
 * after the client had already credited those same samples at the
 * lower multiplier. They'd disagree and replay validation would
 * mismatch.
 */
function processSpinSample(
  state: CirclesEngineState,
  config: CirclesEngineConfig,
  sample: CirclesSpinSample,
  events: CirclesEngineEvent[],
): void {
  // Defensive: server validation rejects non-finite coordinates, but
  // this function is also called directly by the client every frame
  // off live `getCursorPosition()` data. A single NaN sample (browser
  // quirk, pointer-lock edge case, future regression) would poison
  // `state.score` for the rest of the run because `score += NaN` is
  // sticky. Guard at the engine boundary so client and server have
  // the same trust contract. `beat` joins the guard so a hostile
  // audio clock returning NaN can't bypass the spinner-window check
  // (`findActiveSpinner` walks `obj.beat > beat` which is false for
  // NaN, letting the scan iterate without bound).
  if (
    !Number.isFinite(sample.beat) ||
    !Number.isFinite(sample.x) ||
    !Number.isFinite(sample.y)
  ) {
    return;
  }
  const spinner = findActiveSpinner(config.sortedHitObjects, state.resolvedIds, sample.beat);
  if (!spinner) {
    return;
  }
  const dx = sample.x - PLAYFIELD_CENTRE_X;
  const dy = sample.y - PLAYFIELD_CENTRE_Y;
  // Cursor sat exactly on the centre — no defined angle, so the
  // sample contributes nothing this frame. Common in the first
  // pointermove after the cursor enters the playfield from a
  // letterbox / window edge.
  if (dx === 0 && dy === 0) {
    return;
  }
  const angle = Math.atan2(dy, dx);

  let progress = state.spinnerProgress.get(spinner.id);
  if (!progress) {
    progress = {
      rotations: 0,
      lastAngle: null,
      lastSampleBeat: null,
      comboMultiplierAtStart: comboMultiplier(state.combo),
      scoreBanked: 0,
    };
    state.spinnerProgress.set(spinner.id, progress);
  }

  if (progress.lastAngle === null || progress.lastSampleBeat === null) {
    progress.lastAngle = angle;
    progress.lastSampleBeat = sample.beat;
    return;
  }

  const rawDelta = Math.abs(shortestAngleDelta(progress.lastAngle, angle));
  // Cap at the BPM-aware max angular velocity so a doctored replay
  // claiming hyperspeed spins gets clamped — `SPINNER_MAX_RPS` is in
  // rotations per *wall* second, so multiply by how much wall time
  // (derived from beat delta + chart BPM) elapsed since the last
  // sample. Same cap on the client and server because both run this
  // function with the same constants.
  const beatDelta = Math.max(0, sample.beat - progress.lastSampleBeat);
  const beatsPerMs = bpmToBeatsPerMs(config.chart.bpm);
  const wallMs = beatsPerMs > 0 ? beatDelta / beatsPerMs : 0;
  const wallSeconds = wallMs / 1000;
  const maxDelta = SPINNER_MAX_RPS * 2 * Math.PI * wallSeconds;
  // First sample is a free-init (above), so subsequent samples having
  // beatDelta of 0 means a duplicate timestamp — credit nothing
  // rather than zero-multiplying through the cap (which would still
  // be 0 but for clarity we skip).
  const cappedDelta = wallSeconds > 0 ? Math.min(rawDelta, maxDelta) : 0;

  // When `cappedDelta` is 0 the sample contributes nothing — either
  // wall time hasn't advanced (duplicate-beat sample from an audio
  // clock stall) or the cursor genuinely hasn't moved. Skipping
  // *both* the reference-angle/time update **and** the event emit
  // matters: if we updated `lastAngle` here, an audio stall mid-spin
  // would silently discard the actual angular motion between the
  // duplicate-beat samples (the next sample with positive
  // `wallSeconds` would compute its delta against the stalled angle,
  // not the original). Skipping the event also avoids a flood of
  // zero-delta payloads on the headless replay path.
  if (cappedDelta === 0) {
    return;
  }

  // Snapshot the full-rotation count BEFORE we extend progress so we
  // can detect whether this sample crossed a clear-bonus boundary.
  // Floor at the ROTATIONS_THRESHOLD floor so the bonus only fires
  // for rotations strictly past the threshold (the threshold itself
  // is the "clear" — bonuses come from rotations N+1 onward).
  const fullRotsBefore = Math.floor(progress.rotations / (2 * Math.PI));
  progress.rotations += cappedDelta;
  progress.lastAngle = angle;
  progress.lastSampleBeat = sample.beat;
  const fullRotsAfter = Math.floor(progress.rotations / (2 * Math.PI));

  // Per-radian score (always banks). Round-on-emit (not round-per-sample)
  // so total banked score is determined purely by the unrounded
  // `progress.rotations` total — identical between a 30 fps client and
  // a 60 fps client at the same RPS, and identical between the live
  // client run and the server's headless re-feed. Tracking
  // `scoreBanked` on the progress entry lets each tick report its own
  // integer contribution while keeping the total framerate-stable.
  const totalScoreFloat =
    progress.rotations * SPINNER_POINTS_PER_RADIAN * progress.comboMultiplierAtStart;
  const totalScoreInt = Math.floor(totalScoreFloat);
  let scoreDelta = totalScoreInt - progress.scoreBanked;
  progress.scoreBanked = totalScoreInt;

  // Clear bonus: each full rotation strictly past
  // SPINNER_BONUS_ROTATIONS_THRESHOLD adds a flat lump scaled by the
  // spin-start combo multiplier. Crossing multiple boundaries in one
  // sample (rare; e.g. a single sample crediting 4π+ rotations under
  // the cap) adds proportional bonuses, so a sustained spin can't be
  // gamed by feeding sparse samples.
  let bonusScoreDelta = 0;
  let bonusRotationsCrossed = 0;
  if (fullRotsAfter > fullRotsBefore) {
    const bonusBefore = Math.max(0, fullRotsBefore - SPINNER_BONUS_ROTATIONS_THRESHOLD);
    const bonusAfter = Math.max(0, fullRotsAfter - SPINNER_BONUS_ROTATIONS_THRESHOLD);
    const newBonusRotations = bonusAfter - bonusBefore;
    if (newBonusRotations > 0) {
      bonusScoreDelta = Math.round(
        SPINNER_CLEAR_BONUS_PER_ROTATION *
          newBonusRotations *
          progress.comboMultiplierAtStart,
      );
      bonusRotationsCrossed = newBonusRotations;
      scoreDelta += bonusScoreDelta;
    }
  }
  state.score += scoreDelta;
  const rps = cappedDelta / (2 * Math.PI) / wallSeconds;

  const payload: CirclesSpinTickPayload = {
    hitObjectId: spinner.id,
    rotations: progress.rotations,
    deltaRadians: cappedDelta,
    rps,
    beat: sample.beat,
    scoreDelta,
    bonusScoreDelta,
    bonusRotationsCrossed,
  };
  events.push({ type: 'spinner_tick', beat: sample.beat, payload });
}

/**
 * Update slider tracking state from a cursor sample AND fire any
 * unfired tick / repeat / tail crossings whose beat lies at or
 * before this sample, using the sample's freshly-updated tracking
 * state. Tying tick evaluation to the sample (rather than to the
 * generic time-advance) gives us two correctness properties:
 *
 *   1. First-sample ordering: when a slider's first sample arrives,
 *      `processSliderBeatsCrossed` skipped it (`lastSampleBeat ===
 *      null` gate), so the tick at that sample's beat hasn't been
 *      consumed yet. This function fires it using the sample's own
 *      tracking — important for the case where a player's only
 *      sample lands exactly on a tick beat.
 *   2. Tracking attribution between samples: ticks within
 *      `(lastSampleBeat, sampleBeat]` are evaluated by the generic
 *      time-advance using the PRIOR sample's tracking (the new
 *      sample hasn't arrived yet). Sub-frame approximation — the
 *      prior sample is the closest observation when no later sample
 *      exists. Acceptable because tick spacing in real charts is
 *      coarser than typical sample cadence.
 *
 * Sliders with no samples at all (legacy replay shape) credit only
 * the head verdict — the body-component sweep in
 * {@link processSliderBeatsCrossed} skips sliders whose
 * `lastSampleBeat` is still null.
 */
function processSliderSample(
  state: CirclesEngineState,
  config: CirclesEngineConfig,
  sample: CirclesSpinSample,
  events: CirclesEngineEvent[],
): void {
  // Defensive: same trust contract as `processSpinSample` — guard
  // against poison NaN samples at the engine boundary. `beat` is
  // critical: a NaN beat lets the per-slider tick / repeat / tail
  // loops below escape their normal bounds (NaN comparisons all
  // return false), which would fire every remaining body component
  // in a single sample and mark `lastSampleBeat = NaN`, defeating
  // the post-sample sweep's null-skip too.
  if (
    !Number.isFinite(sample.beat) ||
    !Number.isFinite(sample.x) ||
    !Number.isFinite(sample.y)
  ) {
    return;
  }
  const followRadius = config.chart.circleRadius * SLIDER_FOLLOW_RADIUS_MULTIPLIER;
  const followRadiusSq = followRadius * followRadius;
  for (const obj of config.sortedHitObjects) {
    if (obj.kind !== 'slider' || !obj.slider) {
      continue;
    }
    if (obj.beat > sample.beat) {
      // Sortedness: subsequent sliders haven't started either.
      break;
    }
    if (sample.beat > obj.slider.endBeat) {
      // Slider already past — no further sample-driven credit.
      // The post-slider sweep in `processSliderBeatsCrossed` will
      // close out any unfired components with `tracking=false`.
      continue;
    }
    const progress = state.sliderProgress.get(obj.id);
    if (!progress || !progress.headResolved || progress.headVerdict === 'miss') {
      continue;
    }
    if (progress.tailFired) {
      // Tail event has already fired; slider is done. Skip further
      // sample-driven processing so a player who keeps the cursor
      // moving past the slider's end doesn't accidentally re-fire
      // tail or push tracking state forward.
      continue;
    }
    const path = config.sliderPaths.get(obj.id);
    if (!path) {
      continue;
    }
    // Where is the slider ball right now? Distance-to-curve takes
    // the cursor's playfield (x, y) and the curve's playfield
    // points; both share the same coordinate system in the
    // imported chart, so no transform is needed here.
    const arcLen = sliderArcLengthAt({
      headBeat: obj.beat,
      endBeat: obj.slider.endBeat,
      length: obj.slider.length,
      slides: obj.slider.slides,
      beat: sample.beat,
    });
    const ball = pointAtDistance(path, arcLen);
    const dx = sample.x - ball.x;
    const dy = sample.y - ball.y;
    const cursorDistSq = dx * dx + dy * dy;
    const inFollowCircle = cursorDistSq <= followRadiusSq;
    const pressing = sample.pressing === true;
    progress.trackingNow = pressing && inFollowCircle;
    progress.lastSampleBeat = sample.beat;
    // Fire any unfired tick / repeat / tail crossings whose beat
    // lies in (prevLastSampleOrHead, sample.beat] using THIS
    // sample's freshly-updated tracking state.
    const slider = obj.slider;
    while (progress.nextTickIdx < slider.tickBeats.length) {
      const tb = slider.tickBeats[progress.nextTickIdx];
      if (tb > sample.beat) {
        break;
      }
      fireSliderTickAward(state, obj, progress, tb, SLIDER_TICK_SCORE, 'slider_tick', events);
      progress.nextTickIdx += 1;
    }
    while (progress.nextRepeatIdx < slider.repeatBeats.length) {
      const rb = slider.repeatBeats[progress.nextRepeatIdx];
      if (rb > sample.beat) {
        break;
      }
      fireSliderTickAward(
        state,
        obj,
        progress,
        rb,
        SLIDER_REPEAT_SCORE,
        'slider_repeat',
        events,
      );
      progress.nextRepeatIdx += 1;
    }
    if (!progress.tailFired && slider.endBeat <= sample.beat) {
      fireSliderTickAward(
        state,
        obj,
        progress,
        slider.endBeat,
        SLIDER_TAIL_SCORE,
        'slider_tail',
        events,
      );
    }
  }
}

/**
 * Fire score / combo award for a slider tick / repeat / tail crossing.
 * `tracking` reflects the slider's most-recent `trackingNow` snapshot
 * before the crossing — true → award fires (combo+1, score chunk),
 * false → no-op event (renderer can still draw a missed-tick hint).
 */
function fireSliderTickAward(
  state: CirclesEngineState,
  obj: CircleHitObject,
  progress: SliderProgress,
  beat: number,
  baseScore: number,
  eventType: 'slider_tick' | 'slider_repeat' | 'slider_tail',
  events: CirclesEngineEvent[],
): void {
  const tracking = progress.trackingNow;
  let scoreDelta = 0;
  if (tracking) {
    state.combo += 1;
    if (state.combo > state.maxCombo) {
      state.maxCombo = state.combo;
    }
    // Lock score multiplier to head-tap snapshot so client + headless
    // replay produce identical totals (mirrors spinner pattern).
    scoreDelta = Math.round(baseScore * progress.comboMultiplierAtHead);
    state.score += scoreDelta;
    // Slider body components (ticks / repeats) bump score + combo
    // but deliberately do NOT count toward the empty-tap-grace tally
    // (`state.perfects + goods + oks`). Grace is meant to graduate
    // players past onboarding once they've demonstrably *tapped*
    // circles successfully; slider ticks accrue passively while
    // tracking and shouldn't substitute for that signal. The head
    // tap (a regular `judge()` call upstream) does count.
  }
  if (eventType === 'slider_tick') {
    progress.ticksHit += tracking ? 1 : 0;
  } else if (eventType === 'slider_repeat') {
    progress.repeatsHit += tracking ? 1 : 0;
  } else {
    // Tail: emit the event so the renderer can finalise the slider
    // (`tailFired`), and record success-or-not for accuracy /
    // visuals (`tailHit`). Two flags so a tracking=false tail emits
    // exactly once but doesn't pretend to have awarded credit.
    progress.tailHit = tracking;
    progress.tailFired = true;
  }
  events.push({
    type: eventType,
    beat,
    payload: {
      hitObjectId: obj.id,
      beat,
      tracking,
      scoreDelta,
    },
  });
}

/**
 * Post-sample sweep: close out any unfired tick / repeat / tail
 * components on sliders whose endBeat is now <= toBeat AND that
 * received at least one sample during their lifetime. The fallback
 * tracking state is whatever the most-recent sample left in
 * `trackingNow`, so a player who tracked the early ticks then let go
 * of the button gets credit for the early ticks and `tracking=false`
 * fall-throughs for the rest.
 *
 * Sliders that received NO samples at all (legacy replay shape) are
 * skipped entirely — their `lastSampleBeat === null` gates the
 * sweep, so head-only replays produce zero `slider_tick` events
 * just like a head-only chart with no body components.
 *
 * Sample-driven crediting happens in {@link processSliderSample};
 * this function is only the safety net for components whose beats
 * are between the player's last sample and the slider's endBeat.
 */
function processSliderBeatsCrossed(
  state: CirclesEngineState,
  config: CirclesEngineConfig,
  toBeat: number,
  events: CirclesEngineEvent[],
): void {
  for (const obj of config.sortedHitObjects) {
    if (obj.kind !== 'slider' || !obj.slider) {
      continue;
    }
    if (obj.beat > toBeat) {
      break;
    }
    const progress = state.sliderProgress.get(obj.id);
    if (!progress || !progress.headResolved) {
      continue;
    }
    if (progress.headVerdict === 'miss') {
      // Missed head means body components don't credit (osu
      // semantics — a slider-break from a missed head zeros the
      // slider's body).
      continue;
    }
    if (progress.lastSampleBeat === null) {
      // No samples ever landed during this slider's life — treat
      // it as a head-only event (legacy replay shape). Skip the
      // body sweep entirely so we don't pollute the event stream
      // with unanswerable `tracking=false` tick events.
      continue;
    }
    const slider = obj.slider;
    // Only sweep up to the slider's endBeat: ticks past endBeat
    // (which the validator forbids anyway) shouldn't fire.
    const sweepEnd = Math.min(toBeat, slider.endBeat);
    while (progress.nextTickIdx < slider.tickBeats.length) {
      const tb = slider.tickBeats[progress.nextTickIdx];
      if (tb > sweepEnd) {
        break;
      }
      fireSliderTickAward(state, obj, progress, tb, SLIDER_TICK_SCORE, 'slider_tick', events);
      progress.nextTickIdx += 1;
    }
    while (progress.nextRepeatIdx < slider.repeatBeats.length) {
      const rb = slider.repeatBeats[progress.nextRepeatIdx];
      if (rb > sweepEnd) {
        break;
      }
      fireSliderTickAward(
        state,
        obj,
        progress,
        rb,
        SLIDER_REPEAT_SCORE,
        'slider_repeat',
        events,
      );
      progress.nextRepeatIdx += 1;
    }
    if (!progress.tailFired && slider.endBeat <= toBeat) {
      fireSliderTickAward(
        state,
        obj,
        progress,
        slider.endBeat,
        SLIDER_TAIL_SCORE,
        'slider_tail',
        events,
      );
    }
  }
}

function applyPassiveRegen(
  state: CirclesEngineState,
  fromBeat: number,
  toBeat: number,
  events: CirclesEngineEvent[],
): void {
  if (state.combo <= 0) {
    return;
  }
  const quietStart =
    state.lastMissBeat === null ? -Infinity : state.lastMissBeat + HP_REGEN_QUIET_BEATS;
  const eligibleStart = Math.max(fromBeat, quietStart);
  if (eligibleStart >= toBeat) {
    return;
  }
  state.regenProgressBeats += toBeat - eligibleStart;
  while (state.regenProgressBeats >= HP_REGEN_INTERVAL_BEATS) {
    state.regenProgressBeats -= HP_REGEN_INTERVAL_BEATS;
    if (state.hp >= state.maxHp) {
      continue;
    }
    applyHpDelta(state, toBeat, HP_REGEN_PER_TICK, 'regen', events);
  }
}

function advanceTo(
  state: CirclesEngineState,
  config: CirclesEngineConfig,
  toBeat: number,
  events: CirclesEngineEvent[],
): void {
  // NaN guard: without this, a single non-finite `toBeat` poisons
  // `state.beat` permanently — every downstream comparison
  // (`obj.beat > NaN`, `tick.beat <= NaN`, etc.) evaluates `false`,
  // so range-checks silently skip every hit object for the rest of
  // the run. The session has its own top-of-tick guard so this
  // shouldn't fire in practice, but it's cheap belt-and-braces for
  // any future caller (headless runner, future replay paths) that
  // forgets to filter on its side.
  if (!Number.isFinite(toBeat) || toBeat <= state.beat) {
    return;
  }
  const fromBeat = state.beat;
  state.beat = toBeat;
  expireDueAutoResolutions(state, config, toBeat, events);
  if (!state.alive) {
    return;
  }
  // Order matters: slider tick/repeat/tail awards FIRST, then regen.
  // Slider components contribute combo bumps that influence regen
  // eligibility downstream — running regen first would miss them.
  // Auto-resolutions above already include slider HEAD auto-misses
  // (which set sliderProgress[id].headVerdict='miss'); that's why
  // the body-scan honours it by skipping miss-head sliders.
  processSliderBeatsCrossed(state, config, toBeat, events);
  applyPassiveRegen(state, fromBeat, toBeat, events);
}

/** Chronologically-ordered input event for the step loop. */
type ProcessableEvent =
  | { kind: 'tap'; beat: number; input: CirclesEngineInput }
  | { kind: 'spin'; beat: number; sample: CirclesSpinSample };

function handleTap(
  state: CirclesEngineState,
  config: CirclesEngineConfig,
  inp: CirclesEngineInput,
  events: CirclesEngineEvent[],
): void {
  const candidate = findClosestOpenHitObjectForTap(
    config.sortedHitObjects,
    state.resolvedIds,
    inp,
    config.okWindowBeats,
    config.chart.circleRadius,
  );
  if (candidate) {
    resolveCircleHit(state, config, candidate.hitObject, inp, candidate.distance, events);
    events.push({ type: 'tap', beat: inp.beat, payload: { x: inp.x, y: inp.y, hit: true } });
    return;
  }
  // No tap-time hit in the OK window. Before falling through to
  // empty-tap handling, check whether the player tapped on a
  // visible-but-not-yet-hittable circle (approach-window early
  // click). osu! lazer's "shake but don't consume": the circle
  // stays open for a proper later attempt, and we emit a
  // `circle_shake` event so the renderer can jitter the target +
  // surface a "TOO EARLY!" cue. No HP cost, no combo break — the
  // player tried, the game told them they were too early, and
  // they get to try again when the OK window actually opens.
  const earlyClick = findEarlyClickedCircle(
    config.sortedHitObjects,
    state.resolvedIds,
    inp,
    config.approachBeats,
    config.okWindowBeats,
    config.chart.circleRadius,
  );
  if (earlyClick) {
    events.push({
      type: 'circle_shake',
      beat: inp.beat,
      payload: { hitObjectId: earlyClick.hitObject.id },
    });
    // `hit: 'early'` lets the client distinguish the tap visual
    // from a successful hit, an outright miss, and an empty tap.
    // Also suppresses the empty-tap HP penalty below — the player
    // clearly aimed at a real target, just too early.
    events.push({
      type: 'tap',
      beat: inp.beat,
      payload: { x: inp.x, y: inp.y, hit: 'early' },
    });
    return;
  }
  // No circle in the good window + radius and no early click on a
  // visible circle. Fire the tap event so the renderer can flash a
  // "no hit" cursor pulse, then follow the Pulse empty-tap rules:
  // chunk HP only if there's an unresolved object close enough in
  // time that the player could plausibly have been reacting to it,
  // and only after the onboarding grace.
  events.push({ type: 'tap', beat: inp.beat, payload: { x: inp.x, y: inp.y, hit: false } });
  const objectNearby = hasUnresolvedObjectWithinEmptyTapRange(
    config.sortedHitObjects,
    state.resolvedIds,
    inp.beat,
  );
  const successfulHits = state.perfects + state.goods + state.oks;
  if (objectNearby && successfulHits >= HP_EMPTY_TAP_GRACE_HITS) {
    applyHpDelta(state, inp.beat, HP_EMPTY_TAP, 'empty_tap', events);
  }
}

export function step(input: CirclesStepInput): CirclesStepOutput {
  const { config, fromBeat, toBeat } = input;
  const state = input.state;
  const events: CirclesEngineEvent[] = [];

  if (!state.alive || state.completed) {
    return { state, events };
  }
  // Top-level NaN guard. The session bails before it ever calls
  // `step()` with a non-finite beat (see `tick`), but headless
  // runners, future replay paths, and tests can call directly —
  // this keeps the engine resilient to a NaN slipping through any
  // of those entry points. Same rationale as the `advanceTo` guard:
  // NaN comparisons all evaluate `false`, so without this the run
  // would silently skip every range check for the rest of the chart.
  if (!Number.isFinite(fromBeat) || !Number.isFinite(toBeat)) {
    return { state, events };
  }

  // Merge taps + spin samples into a single chronological stream so
  // state.beat advances in lockstep with whichever event comes next.
  // Stable sort: tap before spin sample at the same beat so a tap
  // landing exactly on the spinner end (e.g. final sample as the
  // window closes) doesn't get its own resolution mid-spin processing.
  const merged: ProcessableEvent[] = [];
  for (const inp of input.inputs) {
    if (inp.beat < fromBeat || inp.beat > toBeat) {
      continue;
    }
    merged.push({ kind: 'tap', beat: inp.beat, input: inp });
  }
  if (input.spinSamples) {
    for (const sample of input.spinSamples) {
      if (sample.beat < fromBeat || sample.beat > toBeat) {
        continue;
      }
      merged.push({ kind: 'spin', beat: sample.beat, sample });
    }
  }
  merged.sort((a, b) => {
    if (a.beat !== b.beat) {
      return a.beat - b.beat;
    }
    if (a.kind !== b.kind) {
      return a.kind === 'tap' ? -1 : 1;
    }
    return 0;
  });

  for (const ev of merged) {
    advanceTo(state, config, ev.beat, events);
    if (!state.alive || state.completed) {
      break;
    }
    if (ev.kind === 'tap') {
      handleTap(state, config, ev.input, events);
    } else {
      // Cursor samples drive both spinners (rotation accumulator)
      // and sliders (tracking / tick crediting). Both calls are
      // safe no-ops when no spinner / slider is active for this
      // sample's beat.
      processSpinSample(state, config, ev.sample, events);
      processSliderSample(state, config, ev.sample, events);
    }
    if (!state.alive) {
      break;
    }
  }

  if (state.alive && !state.completed) {
    advanceTo(state, config, toBeat, events);
  }

  if (state.alive && !state.completed && state.beat >= config.chart.totalBeats) {
    // Sweep up any objects whose resolution beat is on/before the
    // chart end. Pass a `toBeat` past the latest possible expiry
    // (`+ okWindowBeats + ε`) so circles whose late-edge sat
    // exactly at totalBeats also get expired.
    expireDueAutoResolutions(
      state,
      config,
      state.beat + config.okWindowBeats + 1e-6,
      events,
    );
    if (state.alive) {
      state.completed = true;
      events.push({ type: 'completed', beat: state.beat });
    }
  }

  return { state, events };
}

export function summariseAccuracy(state: CirclesEngineState): number {
  return computeAccuracy(state.perfects, state.goods, state.oks, state.misses);
}

export { buildHitObjectLookup, MAX_HP };
